import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { vouchersApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { formatCurrency, formatDate, formatDateTime } from '../../lib/utils';
import { Search, FileText, Eye, Clock, Paperclip } from 'lucide-react';
import { toast } from 'sonner';

export const AccountsQueuePage = () => {
  const navigate = useNavigate();
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('awaiting_action');

  useEffect(() => {
    fetchVouchers();
  }, [statusFilter]);

  const fetchVouchers = async () => {
    try {
      let data;
      if (statusFilter === 'all') {
        data = await vouchersApi.getAll(null);
      } else if (statusFilter === 'awaiting_action') {
        // Fetch both INTERNAL_AUDIT_APPROVED and HOLD vouchers
        const [auditApproved, holdVouchers] = await Promise.all([
          vouchersApi.getAll('INTERNAL_AUDIT_APPROVED'),
          vouchersApi.getAll('HOLD')
        ]);
        data = [...auditApproved, ...holdVouchers];
      } else {
        data = await vouchersApi.getAll(statusFilter);
      }
      setVouchers(data);
    } catch (error) {
      toast.error('Failed to fetch vouchers');
    } finally {
      setLoading(false);
    }
  };

  const filteredVouchers = vouchers.filter((v) =>
    v.beneficiaryName?.toLowerCase().includes(search.toLowerCase()) ||
    v.voucherNo?.toLowerCase().includes(search.toLowerCase()) ||
    v.createdByName?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accounts-queue-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-heading font-semibold tracking-tight">Approval Queue</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Process audit-approved vouchers for payment
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by beneficiary, voucher no, employee..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-56" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="awaiting_action">Awaiting Action (incl. Hold)</SelectItem>
                <SelectItem value="INTERNAL_AUDIT_APPROVED">Awaiting Approval</SelectItem>
                <SelectItem value="HOLD">On Hold</SelectItem>
                <SelectItem value="ACCOUNTS_APPROVED">Approved</SelectItem>
                <SelectItem value="PAID">Paid</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Vouchers Table */}
      <Card>
        <CardContent className="p-0">
          {filteredVouchers.length === 0 ? (
            <div className="text-center py-16">
              <Clock className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">No vouchers found</h3>
              <p className="text-muted-foreground text-sm">
                {search ? 'Try adjusting your search' : 'No vouchers in this status'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">Voucher No</TableHead>
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold">Employee</TableHead>
                    <TableHead className="font-semibold">Beneficiary</TableHead>
                    <TableHead className="font-semibold">Amount</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredVouchers.map((voucher) => (
                    <TableRow
                      key={voucher.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => {
                        const canProcess = voucher.status === 'INTERNAL_AUDIT_APPROVED' || 
                                          voucher.status === 'ACCOUNTS_APPROVED' || 
                                          voucher.status === 'HOLD';
                        navigate(canProcess ? `/accounts/process/${voucher.id}` : `/accounts/vouchers/${voucher.id}`);
                      }}
                      data-testid={`voucher-row-${voucher.id}`}
                    >
                      <TableCell className="font-mono font-medium">
                        <div className="flex items-center gap-1">
                          {voucher.voucherNo || '-'}
                          {voucher.attachments && voucher.attachments.length > 0 && (
                            <Paperclip className="w-3.5 h-3.5 text-[#6a5da4]" title={`${voucher.attachments.length} attachment(s)`} />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{formatDate(voucher.date)}</TableCell>
                      <TableCell>{voucher.createdByName}</TableCell>
                      <TableCell>{voucher.beneficiaryName}</TableCell>
                      <TableCell className="font-mono">
                        {formatCurrency(voucher.amountTotal, voucher.currency)}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={voucher.status} size="sm" />
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const canProcess = voucher.status === 'INTERNAL_AUDIT_APPROVED' || 
                                              voucher.status === 'ACCOUNTS_APPROVED' || 
                                              voucher.status === 'HOLD';
                            navigate(canProcess ? `/accounts/process/${voucher.id}` : `/accounts/vouchers/${voucher.id}`);
                          }}
                          data-testid={`process-voucher-${voucher.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" /> 
                          {voucher.status === 'INTERNAL_AUDIT_APPROVED' || voucher.status === 'ACCOUNTS_APPROVED' || voucher.status === 'HOLD' ? 'Process' : 'View'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountsQueuePage;
